import { get } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { FloatLayer, Icon, Loader, Text } from '..'
import { Grid } from '../../context'
import classesBuilder from '../../shared/utils/classesBuilder'
import * as style from './style/buttonActions.module.scss'
import { DropdownProps } from './types/dropdown.types'

const Dropdown: React.FC<DropdownProps> = (
  {
    actionOptions,
    children,
    disabled,
    label,
    loaderType,
    loading,
    maxWidth,
    onClick,
    requestFindLabelFn,
    requestFindLabelParamId,
    requestFindLabelResponseOnLoad,
    requestFindLabelResponsePathText,
    size = 'medium',
    bold,
    color = 'colorDefault',
    invertColor,
    outlined,
  }
) => {
  const [useLabel, setLabel] = useState('')
  const [useLoading, setLoading] = useState(false)
  const [useShowLayer, setShowLayer] = useState(false)
  const showHideLayer = (): void => setShowLayer(!useShowLayer)
  const loadLabel = useCallback(async (): Promise<void> => {
    if (
      !requestFindLabelFn ||
      !requestFindLabelParamId ||
      !requestFindLabelResponsePathText
    ) {
      return
    }

    try {
      setLoading(true)
      const response = await requestFindLabelFn?.(requestFindLabelParamId)
      const label = get(response, requestFindLabelResponsePathText)
      setLabel(label)
      requestFindLabelResponseOnLoad?.(label)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [
    requestFindLabelResponseOnLoad,
    requestFindLabelParamId,
    requestFindLabelResponsePathText,
    requestFindLabelFn,
  ])

  useEffect(() => {
    loadLabel();
  }, [loadLabel])

  return (
    <div
      className={classesBuilder(style, {
        [color]: color,
        buttonActionsWrapper: true,
        invertColor,
        noPrincipalClick: !onClick,
        outlined,
      })}
    >
      <Grid alignItems='center'>
        <button
          disabled={disabled}
          onClick={onClick || showHideLayer}
          className={classesBuilder(style, {
            buttonActions: true,
            disabled,
            iconOnly: !label,
            loading,
            [size]: size,
            pointerEventNone: useShowLayer && !onClick
          })}
          type='button'
          style={{
            maxWidth
          }}
        >
          <>
            {(loading || useLoading) && (
              <div className={classesBuilder(style, { loader: true })}>
                <Loader color='colorDefault' type={loaderType} />
              </div>
            )}

            <div className={classesBuilder(style, {
              text: true,
              bold,
            })}>
              {useLabel || children || label}
            </div>
          </>
        </button>

        {
          !!actionOptions?.length && (
            <button
              disabled={disabled}
              onClick={showHideLayer}
              className={classesBuilder(style, {
                buttonActionOptions: true,
                disabled,
                [size || '']: size,
                flip: useShowLayer,
                pointerEventNone: useShowLayer
              })}
              type='button'
            >
              <Icon name='arrow-down' />
            </button>
          )
        }
      </Grid>

      <div
        className={classesBuilder(style, { boxFloatLayer: true })}
      >
        <FloatLayer
          show={useShowLayer}
          onClose={showHideLayer}
          outlined
          rounded
          elevation={6}
          full
        >
          {
            actionOptions?.map((actionOption, key) => (
              <button
                key={key}
                className={classesBuilder(style, { buttonActionOption: true })}
                onClick={() => {
                  actionOption.onClick?.()
                  showHideLayer()
                }}
              >
                <Grid
                  alignItems='center'
                  alignContent='right'
                >
                  {
                    actionOption.icon && (
                      <div
                        className={classesBuilder(style, { iconSquare: true })}
                      >
                        <Icon color='colorDefault' name={actionOption.icon} />
                      </div>
                    )
                  }

                  <Text size='medium' value={actionOption.label}/>
                </Grid>
              </button>
            ))
          }
        </FloatLayer>
      </div>
    </div>
  )
}

export default Dropdown
